import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import Loader from 'components/partials/loader/loader';
import Breadcrumbs from 'components/partials/beadcrumbs/breadcrumbs';
import CriticalElement from 'containers/admin/clients/client/tabs-components/critical-elements/critical-elements-manager/critical-element/critical-element';
import ResponsiveWrapper from 'components/shared/others/responsive-wrapper/responsive-wrapper';

import { getClientCriticalElementById } from 'api/generated/critical-elements/critical-elements';

import {
  getBreadcrumbsForCriticalElement,
  getCommonRedirectLink,
} from 'containers/admin/clients/client/tabs-components/critical-elements/critical-elements-manager/critical-elements.helpers';

import { useClient } from 'hooks/api/use-clients';
import { useCurrentUser } from 'hooks/api/use-current-user';

import type { CriticalElement as CriticalElementType } from 'containers/admin/clients/client/tabs-components/goals-tab/types';

interface CurrentUserShape {
  data?: { oktaUserId?: string };
}

const CriticalElementPage = (): React.JSX.Element => {
  const params = useParams<{
    clientId: string;
    year: string;
    id: string;
  }>();
  const navigate = useNavigate();
  const { clientId, year, id: elementId } = params;
  const isNew = elementId === 'new';
  const redirectLink = getCommonRedirectLink(clientId, year ?? '');

  const currentUser = useCurrentUser() as CurrentUserShape;
  const userId = currentUser?.data?.oktaUserId;

  const [isEditMode, setIsEditMode] = useState<boolean>(isNew);
  const [criticalElement, setCriticalElement] =
    useState<CriticalElementType | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  const fetchCriticalElement = useCallback(async (): Promise<void> => {
    if (!userId || !clientId || !elementId) return;
    setLoading(true);
    try {
      const data = await getClientCriticalElementById(
        userId,
        Number(clientId),
        Number(elementId)
      );

      setCriticalElement({
        ...data,
        details: data.details?.map((detail) => ({
          ...detail,
          focusArea: detail.focusArea ?? undefined,
          subArea: detail.subArea ?? undefined,
        })),
        // TODO(ts-migration): OpenAPI DTO vs CriticalElement UI shape — migration-debt.md (2026-05-07 CE migration)
      } as CriticalElementType);
    } catch (error) {
      console.error('Error fetching critical element:', error);
    } finally {
      setLoading(false);
    }
  }, [elementId, clientId, userId, year]);

  useEffect(() => {
    if (!isNew) {
      fetchCriticalElement();
    } else {
      setLoading(false);
    }
  }, []);

  const { data: currentClient } = useClient(clientId);

  const handleSaveSuccess = (): void => {
    navigate(redirectLink);
  };

  const handleCancel = (): void => {
    navigate(redirectLink);
  };

  if (loading) {
    return <Loader loading />;
  }

  return (
    <ResponsiveWrapper>
      <Breadcrumbs
        breadcrumbs={getBreadcrumbsForCriticalElement(
          currentClient,
          year ?? ''
        )}
      />
      <CriticalElement
        data={criticalElement}
        isEditMode={isEditMode}
        setIsEditMode={setIsEditMode}
        loading={loading}
        getElement={fetchCriticalElement}
        onSaveSuccess={handleSaveSuccess}
        onCancel={handleCancel}
        setLoading={setLoading}
      />
    </ResponsiveWrapper>
  );
};

export default CriticalElementPage;
